/**
 * Client-side Instagram (Meta) media validation: allowed types and size limits.
 * Use before calling upload API to show a toast instead of server error.
 */

const IMAGE_MB = 8;
const VIDEO_AUDIO_DOC_MB = 25;

const IMAGE_MIMES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
]);
const VIDEO_MIMES = new Set([
  "video/mp4",
  "video/ogg",
  "video/avi",
  "video/quicktime", // MOV
  "video/webm",
]);
const AUDIO_MIMES = new Set([
  "audio/aac",
  "audio/mp4",
  "audio/x-m4a",
  "audio/wav",
  "audio/mpeg",
]);
const DOC_MIMES = new Set([
  "application/pdf",
]);

export const INSTAGRAM_FORMAT_MESSAGE =
  "Instagram supports: Image (PNG, JPEG, GIF) up to 8 MB; Video (MP4, OGG, AVI, MOV, WebM) up to 25 MB; Audio (AAC, M4A, WAV, MP4) up to 25 MB; Document (PDF) up to 25 MB.";

/**
 * @param {File} file
 * @returns {{ valid: boolean; error?: string }}
 */
export function validateInstagramFile(file) {
  if (!file || !file.size) return { valid: false, error: "No file selected." };
  const mime = (file.type || "").toLowerCase();
  const sizeBytes = file.size;

  if (IMAGE_MIMES.has(mime)) {
    if (sizeBytes > IMAGE_MB * 1024 * 1024) {
      return { valid: false, error: `Image must be ${IMAGE_MB} MB or less.` };
    }
    return { valid: true };
  }
  if (VIDEO_MIMES.has(mime)) {
    if (sizeBytes > VIDEO_AUDIO_DOC_MB * 1024 * 1024) {
      return { valid: false, error: `Video must be ${VIDEO_AUDIO_DOC_MB} MB or less.` };
    }
    return { valid: true };
  }
  if (AUDIO_MIMES.has(mime) || DOC_MIMES.has(mime)) {
    if (sizeBytes > VIDEO_AUDIO_DOC_MB * 1024 * 1024) {
      return { valid: false, error: `File must be ${VIDEO_AUDIO_DOC_MB} MB or less.` };
    }
    return { valid: true };
  }

  return { valid: false, error: INSTAGRAM_FORMAT_MESSAGE };
}

/**
 * For image-only picker (e.g. Generic card).
 * @param {File} file
 */
export function validateInstagramImageFile(file) {
  if (!file || !file.size) return { valid: false, error: "No file selected." };
  const mime = (file.type || "").toLowerCase();
  const sizeBytes = file.size;
  if (!IMAGE_MIMES.has(mime)) {
    return { valid: false, error: "Image must be PNG, JPEG, or GIF." };
  }
  if (sizeBytes > IMAGE_MB * 1024 * 1024) {
    return { valid: false, error: `Image must be ${IMAGE_MB} MB or less.` };
  }
  return { valid: true };
}

/**
 * For media template (image or video).
 * @param {File} file
 */
export function validateInstagramMediaTemplateFile(file) {
  if (!file || !file.size) return { valid: false, error: "No file selected." };
  const mime = (file.type || "").toLowerCase();
  const sizeBytes = file.size;
  if (IMAGE_MIMES.has(mime)) {
    if (sizeBytes > IMAGE_MB * 1024 * 1024) {
      return { valid: false, error: `Image must be ${IMAGE_MB} MB or less.` };
    }
    return { valid: true };
  }
  if (VIDEO_MIMES.has(mime)) {
    if (sizeBytes > VIDEO_AUDIO_DOC_MB * 1024 * 1024) {
      return { valid: false, error: `Video must be ${VIDEO_AUDIO_DOC_MB} MB or less.` };
    }
    return { valid: true };
  }
  return { valid: false, error: "Media must be image (PNG, JPEG, GIF) or video (MP4, OGG, AVI, MOV, WebM)." };
}
